import { entityRefToUrn } from '@agilelab/plugin-wb-builder-common';
import { configurationApiRef } from '@agilelab/plugin-wb-configuration-common';
import {
  batchComputePolicyViolationsCount,
  governanceApiRef,
} from '@agilelab/plugin-wb-governance';
import hashObject from 'object-hash';
import { PolicyViolationsCountMap } from '@agilelab/plugin-wb-governance-common';
import {
  decodeBase64PageCursor,
  encodeBase64PageCursor,
  EnvironmentsContextType,
  useEnvironmentsContext,
  useQueryParamState,
} from '@agilelab/plugin-wb-platform';
import {
  ALL_TAXONOMIES_FILTER,
  practiceShaperApiRef,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import {
  adaptFilters,
  FilterResolversOverride,
  FilterState,
  SearchFiltersContext,
  useFilterStateValidation,
  useSearchFilterConfig,
  useSearchFilters,
  witboostSearchApiRef,
} from '@agilelab/plugin-wb-search';
import {
  CollatorType,
  ComplexFilter,
  JsonObjectSearchFilterVisitor,
  MARKETPLACE_FAVORITES,
  MARKETPLACE_PROJECTS_CONSUMABLE,
  MARKETPLACE_PROJECTS_DATA_LANDSCAPE,
  MARKETPLACE_PROJECTS_ENVIRONMENT,
  OrderByOption,
  QueryOperator,
  SEARCH_MARKETPLACE_UI_CONFIG,
  SearchFilterConfigType,
  WitboostMarketplaceSearchResultSet,
} from '@agilelab/plugin-wb-search-common';
import { useApolloClient } from '@apollo/client';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import usePrevious from 'react-use/esm/usePrevious';
import {
  buildDomainTrees,
  getAllExternalIdsMap,
  getDomainTreesByTypes,
} from '../../../utils';
import { MarketplaceSearchDocumentEnrichedResultSet } from '../types';
import {
  enrichSearchResults,
  getCompatibleDomainTypes,
  typeFilterResolver,
} from '../utils';
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { marketplaceCatalogSearchedTopic } from '@agilelab/plugin-wb-events-common';
import { marketplaceApiRef } from '../../../plugin';

type Search = {
  view: ViewType;
  setView: (newParams: ViewType) => void;
  environment: EnvironmentsContextType['environment'];
  policyViolationsInfo: UseQueryResult<PolicyViolationsCountMap>;
  limit: number;
  setLimit: (limit: number) => void;
  cursor?: string;
  filterStateValid?: boolean;
  setCursor: Dispatch<SetStateAction<string | undefined>>;
  filters?: FilterState | undefined;
  setFilters: Dispatch<SetStateAction<FilterState | undefined>>;
  query?: string;
  setQuery: (query: string) => void;
  filtersContext: SearchFiltersContext;
  favoriteFilter: boolean;
  setFavoriteFilter: Dispatch<SetStateAction<boolean>>;
  setOrderByOption: Dispatch<SetStateAction<OrderByOption>>;
  orderByOption: OrderByOption;
} & UseQueryResult<MarketplaceSearchDocumentEnrichedResultSet>;

const defaultSearchContext = null;

export const SearchContext = createContext<Search | null>(defaultSearchContext);

const DEFAULT_LIMIT = 15;

// consumable should ALWAYS be true
const DEFAULT_FILTERS = {
  [MARKETPLACE_PROJECTS_CONSUMABLE.label]: 'true',
  [MARKETPLACE_FAVORITES.label]: 'false',
};

export const useSearchContext = () => {
  const contextValue = useContext(SearchContext);

  if (!contextValue) {
    throw new Error(
      'useSearchContext has to be used within <SearchContext.Provider>',
    );
  }

  return contextValue;
};

export type ViewType = 'cards' | 'table';

export const SearchContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const searchApi = useApi(witboostSearchApiRef);
  const configApi = useApi(configApiRef);
  const marketplaceApi = useApi(marketplaceApiRef);
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const wbConfigApi = useApi(configurationApiRef);
  const practiceShaperApi = useApi(practiceShaperApiRef);
  const identityApi = useApi(identityApiRef);
  const governanceApi = useApi(governanceApiRef);

  const [didSetDefaults, setDidSetDefaults] = useState(false);

  const { publish } = usePublishEvent({ generatorSystem: 'marketplace' });
  const { environment } = useEnvironmentsContext();
  const { selectedTaxonomyRef } = useTaxonomySelection();
  const { starredEntities } = useStarredEntities();

  const { filtersConfig, ready } = useSearchFilterConfig(
    CollatorType.MARKETPLACE_PROJECTS,
    selectedTaxonomyRef,
  );

  const [favoriteFilter, setFavoriteFilter] = useState<boolean>(false);
  const [query, setQuery] = useQueryParamState<string>('query');
  const [filters, setFilters] = useQueryParamState<FilterState | undefined>(
    'filters',
    undefined,
    {},
  );

  const [orderByOption, setOrderByOption] =
    useQueryParamState<OrderByOption>('orderBy');

  const [cursor, setCursor] = useQueryParamState<string | undefined>('cursor');
  const [limit, setLimit] = useQueryParamState<string | undefined>('limit');
  const [view, setView] = useQueryParamState<ViewType>(
    'view',
    undefined,
    (configApi.getOptionalString(
      `${SEARCH_MARKETPLACE_UI_CONFIG}.defaultView`,
    ) as ViewType) ?? 'cards',
  );

  const setLimitNumber = useCallback(
    (newLimit: number) => {
      setLimit(newLimit.toString());
    },
    [setLimit],
  );

  const defaultFilters = useMemo(() => {
    if (!ready) return undefined;
    return filtersConfig.reduce((acc, item) => {
      if (item.defaultValues) {
        acc[item.label] = item.defaultValues;
      }
      return acc;
    }, {} as Record<string, string[]>);
  }, [filtersConfig, ready]);

  const filterResolversOverride = useMemo<FilterResolversOverride>(
    () => ({
      [SearchFilterConfigType.DOMAIN]: async values => {
        const compatibleDomainTypes = await getCompatibleDomainTypes({
          selectedTaxonomyRef,
          practiceShaperApi,
          identityApi,
        });
        const data = await getDomainTreesByTypes(
          compatibleDomainTypes ?? [],
          apolloClient,
        );
        return filter => {
          const availableDomains = new Set(
            values[filter.label].map(v => v.value),
          );

          // filters out domains which are not in available values
          return buildDomainTrees(data, d => availableDomains.has(d.id));
        };
      },
      [SearchFilterConfigType.TYPE]: async values => {
        const data = await typeFilterResolver(
          practiceShaperApi,
          selectedTaxonomyRef !== ALL_TAXONOMIES_FILTER
            ? selectedTaxonomyRef
            : undefined,
        );
        return filter => {
          if (!values?.[filter.label]) {
            return [];
          }
          const availableTypes = new Map(
            values[filter.label].map(v => [v.value, v]),
          );
          // filters out types which are not in available values
          return data.filter(i => availableTypes.has(i.value));
        };
      },
    }),
    [apolloClient, identityApi, practiceShaperApi, selectedTaxonomyRef],
  );

  useEffect(() => {
    if (!ready) return;
    if (!defaultFilters) return;
    if (didSetDefaults) return;

    // applies default filters only if there are no filters in the url or in the state, if there are filters it means that the user has already customized them and we don't want to override his choices
    const hasFilters = Object.keys(filters ?? {}).length > 0;
    if (!hasFilters) {
      setFilters(defaultFilters as any);
    }
    setDidSetDefaults(true);
  }, [ready, defaultFilters, filters, setFilters, didSetDefaults]);

  // merge the values for taxonomy, environment and consumable with the filters
  const completeFilters = useMemo(() => {
    if (!ready || !didSetDefaults) return undefined;

    // add environment and favorites since they are handled outside filters
    const extraValues = {
      [MARKETPLACE_PROJECTS_ENVIRONMENT.label]: [environment.name],
      [MARKETPLACE_FAVORITES.label]: favoriteFilter.toString(),
    };

    // add taxonomy since it's handled outside filters
    if (selectedTaxonomyRef !== ALL_TAXONOMIES_FILTER)
      extraValues[MARKETPLACE_PROJECTS_DATA_LANDSCAPE.label] = [
        entityRefToUrn(selectedTaxonomyRef),
      ];

    return { ...DEFAULT_FILTERS, ...extraValues, ...filters };
  }, [
    filters,
    selectedTaxonomyRef,
    environment,
    favoriteFilter,
    ready,
    didSetDefaults,
  ]);

  // filters used for search query
  const jsonSearchFilter = useMemo(() => {
    if (!completeFilters || !ready) return undefined;

    const adaptedFilters = adaptFilters(completeFilters ?? {}, filtersConfig, [
      ...starredEntities,
    ]);
    return new ComplexFilter(QueryOperator.AND, [...adaptedFilters]).accept(
      new JsonObjectSearchFilterVisitor(),
    );
  }, [completeFilters, filtersConfig, ready, starredEntities]);

  const searchFiltersConfigHash = useMemo(() => {
    if (!jsonSearchFilter) return undefined;

    return hashObject(jsonSearchFilter, {
      unorderedObjects: true,
      unorderedArrays: true,
    });
  }, [jsonSearchFilter]);

  const prevSearchFiltersConfigHash = usePrevious(searchFiltersConfigHash);
  const prevQuery = usePrevious(query);
  const prevLimit = usePrevious(limit);
  // Any time the filters configuration changes, we want to start from page 0.
  const effectiveCursor = useMemo(() => {
    const filtersChanged =
      prevSearchFiltersConfigHash !== undefined &&
      searchFiltersConfigHash !== prevSearchFiltersConfigHash;

    const queryChanged = prevQuery !== undefined && query !== prevQuery;
    const limitChanged = prevLimit !== undefined && limit !== prevLimit;

    // if filters, query or limit changed and we are not already in the first page, reset the cursor to go back to the first page.
    if (filtersChanged || queryChanged || limitChanged) {
      if (cursor && decodeBase64PageCursor(cursor) > 0) {
        return encodeBase64PageCursor(0);
      }
    }

    return cursor ?? encodeBase64PageCursor(0);
  }, [
    searchFiltersConfigHash,
    prevSearchFiltersConfigHash,
    query,
    prevQuery,
    limit,
    prevLimit,
    cursor,
  ]);

  // filters used in filters panel
  const filtersContext = useSearchFilters(
    CollatorType.MARKETPLACE_PROJECTS,
    filterResolversOverride,
    jsonSearchFilter,
    query,
    selectedTaxonomyRef,
  );

  // make a query to searchApi whenever the search params change
  const res = useQuery<MarketplaceSearchDocumentEnrichedResultSet>({
    queryKey: [
      'search-results',
      searchFiltersConfigHash,
      query,
      orderByOption,
      limit,
      effectiveCursor,
    ],
    staleTime: 0,
    queryFn: async () => {
      const resultSet = (await searchApi.query({
        term: query ?? '',
        pageCursor: effectiveCursor,
        pageLimit: limit ? Number(limit) : DEFAULT_LIMIT,
        filters: jsonSearchFilter,
        orderBy: orderByOption ?? { field: 'rank', order: 'desc' },
        types: [CollatorType.MARKETPLACE_PROJECTS],
      })) as WitboostMarketplaceSearchResultSet;

      const enrichedResultSet: MarketplaceSearchDocumentEnrichedResultSet = {
        ...resultSet,
        results: await enrichSearchResults(
          resultSet.results,
          apolloClient,
          practiceShaperApi,
          queryClient,
          wbConfigApi,
        ),
      };

      publish(marketplaceCatalogSearchedTopic, {
        filters: {
          search: query,
          ...completeFilters,
        },
      });

      return enrichedResultSet;
    },
    enabled: !!jsonSearchFilter,
  });

  useEffect(() => {
    if (effectiveCursor !== cursor) {
      setCursor(effectiveCursor);
    }
  }, [effectiveCursor, cursor, setCursor]);

  // Keeps the filter state valid against the available values of every filter;
  const filterStateValid = useFilterStateValidation(
    filters,
    setFilters,
    filtersContext.availableValues,
    filtersConfig,
  );
  const policyViolationsSubjects = useMemo(
    () =>
      res.data?.results
        .filter(r => r.document.id && r.document.documentId)
        .map(r => ({
          id: r.document.documentId,
          externalId: r.document.id,
        })) ?? [],
    [res.data],
  );

  const policyViolationsInfo = useQuery({
    queryKey: [
      'marketplace-search-policy-violations-count',
      policyViolationsSubjects,
      environment,
    ],
    queryFn: async ({ signal }) => {
      if (res.isLoading || !res.data || policyViolationsSubjects.length === 0) {
        return new Map();
      }

      const aggregatePolicies =
        configApi.getOptionalBoolean(
          `${SEARCH_MARKETPLACE_UI_CONFIG}.aggregatePolicies`,
        ) ?? true;

      const itemsWithDescendants = aggregatePolicies
        ? await marketplaceApi.getConsumableDescendantsForEntityUrns({
            entityUrns: policyViolationsSubjects.map(i => i.externalId),
            environment: environment.name,
          })
        : policyViolationsSubjects;

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const allExternalIds = getAllExternalIdsMap(itemsWithDescendants);

      const externalIdToCount = await batchComputePolicyViolationsCount(
        allExternalIds,
        governanceApi,
        identityApi,
        environment.name,
        300,
        signal,
      );

      return externalIdToCount;
    },
  });

  const value = {
    view: view ?? 'cards',
    setView,
    environment,
    filters: completeFilters,
    setFilters,
    orderByOption: orderByOption ?? { field: 'rank', order: 'desc' },
    setOrderByOption,
    limit: limit ? Number(limit) : DEFAULT_LIMIT,
    setLimit: setLimitNumber,
    filterStateValid,
    cursor,
    setCursor,
    query,
    setQuery,
    policyViolationsInfo,
    filtersContext,
    setFavoriteFilter,
    favoriteFilter,
    ...res,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};
